import {
    Button,
    Grid,
    Stack,
    Tooltip,
    Typography,
    Box,
    IconButton,
    TextField,
    Alert,
    Chip,
    CircularProgress,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import { useState, useEffect } from 'react';
import {
    Check,
    Copy,
    Home,
    Lock,
    Pencil,
    Settings,
    Shield,
    Upload,
    User,
} from 'lucide-react';
import {
    Form,
    Labeled,
    TextInput,
    useDataProvider,
    useGetIdentity,
    useGetOne,
    useNotify,
    useRecordContext,
} from 'react-admin';
import { useFormState } from 'react-hook-form';
import ImageEditorField from '../misc/ImageEditorField';
import { CrmDataProvider } from '../providers/types';
import { getVerifiedFactors } from '../providers/supabase/mfa';
import { Sale } from '../types';
import { useMutation } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

// Create interfaces for our tab panels
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

// TabPanel component
function _TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

// Add these arrays for the available options
const ALL_STATES = ['Idaho', 'Montana', 'Oregon', 'Washington', 'Wyoming'];

const ALL_CARRIERS = [
    'AgriSompo',
    'Great American',
    'NAU',
    'Pro Ag',
    'RCIS',
    'Rain & Hail',
];

// Create a SelectableChips component
const _SelectableChips = ({
    options,
    selectedValues,
    onChange,
    name: _name,
}: {
    options: string[];
    selectedValues: string[];
    onChange: (newValues: string[]) => void;
    name: string;
}) => {
    const handleToggle = (value: string) => {
        const newSelected = selectedValues.includes(value)
            ? selectedValues.filter(v => v !== value)
            : [...selectedValues, value];
        onChange(newSelected);
    };

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {options.map(option => {
                const isSelected = selectedValues.includes(option);
                return (
                    <Chip
                        key={option}
                        label={option}
                        onClick={() => handleToggle(option)}
                        sx={{
                            bgcolor: isSelected ? '#508A88' : '#f5f5f5',
                            color: isSelected ? 'white' : 'text.primary',
                            '&:hover': {
                                bgcolor: isSelected ? '#3e6967' : '#e0e0e0',
                            },
                            cursor: 'pointer',
                        }}
                    />
                );
            })}
        </Box>
    );
};

export const SettingsPage = () => {
    const [selectedTab, setSelectedTab] = useState('profile');
    const [isEditMode, setEditMode] = useState(false);
    const { identity, refetch: refetchIdentity } = useGetIdentity();
    const { data, refetch: refetchUser } = useGetOne('sales', {
        id: identity?.id,
    });
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();

    // Add password change mutation
    const { mutate: updatePassword } = useMutation({
        mutationKey: ['updatePassword'],
        mutationFn: async () => {
            if (!identity) {
                throw new Error('Record not found');
            }
            return dataProvider.updatePassword(identity.id);
        },
        onSuccess: () => {
            notify(
                'A reset password email has been sent to your email address'
            );
        },
        onError: e => {
            notify(`${e}`, {
                type: 'error',
            });
        },
    });

    const _handleClickOpenPasswordChange = () => {
        updatePassword();
    };

    const { mutate } = useMutation({
        mutationKey: ['signup'],
        mutationFn: async (data: Partial<Sale>) => {
            if (!identity) {
                throw new Error('Record not found');
            }
            return dataProvider.salesUpdate(identity.id, data);
        },
        onSuccess: () => {
            refetchIdentity();
            refetchUser();
            setEditMode(false);
            notify('Your profile has been updated');
        },
        onError: _ => {
            notify('An error occurred. Please try again', {
                type: 'error',
            });
        },
    });

    const handleOnSubmit = async (values: any) => {
        mutate(values);
    };

    const renderContent = () => {
        switch (selectedTab) {
            case 'profile':
                return (
                    <Form onSubmit={handleOnSubmit} record={data}>
                        <SettingsForm
                            isEditMode={isEditMode}
                            setEditMode={setEditMode}
                        />
                    </Form>
                );
            case 'agent':
                return <AgentDetailsTab />;
            case 'password':
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography
                            sx={{
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                color: '#0f172a',
                                mb: 3,
                            }}
                        >
                            Update Profile Security
                        </Typography>
                        <PasswordChangeForm />
                    </Box>
                );
            case 'security':
                return <SecurityTab />;
            default:
                return null;
        }
    };

    if (!identity) return null;

    return (
        <Box
            sx={{
                bgcolor: '#F8FAFC',
                minHeight: '100vh',
                p: 3,
            }}
        >
            <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
                {/* Breadcrumb section */}
                <Box
                    sx={{
                        mb: 3,
                        p: 1.5,
                        bgcolor: 'white',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid #e2e8f0',
                    }}
                >
                    <RouterLink
                        to="/"
                        style={{
                            color: 'inherit',
                            textDecoration: 'none',
                            display: 'flex',
                        }}
                    >
                        <Home
                            size={18}
                            color="#64748b"
                            style={{ marginRight: 8 }}
                        />
                    </RouterLink>
                    <Typography
                        component={RouterLink}
                        to="/settings"
                        sx={{
                            fontSize: '0.875rem',
                            color: '#64748b',
                            textDecoration: 'none',
                            '&:hover': {
                                color: '#0f172a',
                            },
                        }}
                    >
                        Account Profile
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            mx: 1,
                            fontSize: '0.875rem',
                            color: '#94a3b8',
                        }}
                    >
                        /
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '0.875rem',
                            color: '#0f172a',
                            fontWeight: 500,
                        }}
                    >
                        {selectedTab === 'profile'
                            ? 'User Profile'
                            : selectedTab === 'agent'
                              ? 'Agent Details'
                              : selectedTab === 'security'
                                ? 'Security'
                                : 'Change Password'}
                    </Typography>
                </Box>

                {/* Main Content with Left Navigation */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Left Navigation */}
                    <Paper
                        sx={{
                            width: 240,
                            height: 'fit-content',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            boxShadow: 'none',
                        }}
                    >
                        <List>
                            <ListItem
                                button
                                selected={selectedTab === 'profile'}
                                onClick={() => setSelectedTab('profile')}
                                sx={{
                                    borderRadius: '6px',
                                    mx: 0.5,
                                    mb: 0.5,
                                    '&.Mui-selected': {
                                        bgcolor: '#ecfdf5',
                                        color: '#047857',
                                        '&:hover': {
                                            bgcolor: '#d1fae5',
                                        },
                                        '& .MuiListItemIcon-root': {
                                            color: '#047857',
                                        },
                                        '& .MuiListItemText-secondary': {
                                            color: '#059669',
                                        },
                                    },
                                    '&:not(.Mui-selected)': {
                                        color: '#475569',
                                        '&:hover': {
                                            color: '#0f172a',
                                            bgcolor: '#f8fafc',
                                        },
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <User
                                        size={18}
                                        color={
                                            selectedTab === 'profile'
                                                ? '#047857'
                                                : '#64748b'
                                        }
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary="User Profile"
                                    secondary="Profile Settings"
                                    primaryTypographyProps={{
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                    }}
                                    secondaryTypographyProps={{
                                        fontSize: '0.75rem',
                                    }}
                                />
                            </ListItem>

                            <ListItem
                                button
                                selected={selectedTab === 'agent'}
                                onClick={() => setSelectedTab('agent')}
                                sx={{
                                    borderRadius: '6px',
                                    mx: 0.5,
                                    mb: 0.5,
                                    '&.Mui-selected': {
                                        bgcolor: '#ecfdf5',
                                        color: '#047857',
                                        '&:hover': {
                                            bgcolor: '#d1fae5',
                                        },
                                        '& .MuiListItemIcon-root': {
                                            color: '#047857',
                                        },
                                        '& .MuiListItemText-secondary': {
                                            color: '#059669',
                                        },
                                    },
                                    '&:not(.Mui-selected)': {
                                        color: '#475569',
                                        '&:hover': {
                                            color: '#0f172a',
                                            bgcolor: '#f8fafc',
                                        },
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Settings
                                        size={18}
                                        color={
                                            selectedTab === 'agent'
                                                ? '#047857'
                                                : '#64748b'
                                        }
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Agent Details"
                                    secondary="Crop Insurance Preferences"
                                    primaryTypographyProps={{
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                    }}
                                    secondaryTypographyProps={{
                                        fontSize: '0.75rem',
                                    }}
                                />
                            </ListItem>

                            <ListItem
                                button
                                selected={selectedTab === 'password'}
                                onClick={() => setSelectedTab('password')}
                                sx={{
                                    borderRadius: '6px',
                                    mx: 0.5,
                                    mb: 0.5,
                                    '&.Mui-selected': {
                                        bgcolor: '#ecfdf5',
                                        color: '#047857',
                                        '&:hover': {
                                            bgcolor: '#d1fae5',
                                        },
                                        '& .MuiListItemIcon-root': {
                                            color: '#047857',
                                        },
                                        '& .MuiListItemText-secondary': {
                                            color: '#059669',
                                        },
                                    },
                                    '&:not(.Mui-selected)': {
                                        color: '#475569',
                                        '&:hover': {
                                            color: '#0f172a',
                                            bgcolor: '#f8fafc',
                                        },
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Lock
                                        size={18}
                                        color={
                                            selectedTab === 'password'
                                                ? '#047857'
                                                : '#64748b'
                                        }
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Change Password"
                                    secondary="Update profile Security"
                                    primaryTypographyProps={{
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                    }}
                                    secondaryTypographyProps={{
                                        fontSize: '0.75rem',
                                    }}
                                />
                            </ListItem>

                            <ListItem
                                button
                                selected={selectedTab === 'security'}
                                onClick={() => setSelectedTab('security')}
                                sx={{
                                    borderRadius: '6px',
                                    mx: 0.5,
                                    mb: 0.5,
                                    '&.Mui-selected': {
                                        bgcolor: '#ecfdf5',
                                        color: '#047857',
                                        '&:hover': {
                                            bgcolor: '#d1fae5',
                                        },
                                        '& .MuiListItemIcon-root': {
                                            color: '#047857',
                                        },
                                        '& .MuiListItemText-secondary': {
                                            color: '#059669',
                                        },
                                    },
                                    '&:not(.Mui-selected)': {
                                        color: '#475569',
                                        '&:hover': {
                                            color: '#0f172a',
                                            bgcolor: '#f8fafc',
                                        },
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Shield
                                        size={18}
                                        color={
                                            selectedTab === 'security'
                                                ? '#047857'
                                                : '#64748b'
                                        }
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Security"
                                    secondary="Two-Factor Authentication"
                                    primaryTypographyProps={{
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                    }}
                                    secondaryTypographyProps={{
                                        fontSize: '0.75rem',
                                    }}
                                />
                            </ListItem>
                        </List>
                    </Paper>

                    {/* Content Area */}
                    <Paper
                        sx={{
                            flex: 1,
                            p: 3,
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            boxShadow: 'none',
                        }}
                    >
                        {renderContent()}
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};

const SettingsForm = ({
    isEditMode,
    setEditMode,
}: {
    isEditMode: boolean;
    setEditMode: (value: boolean) => void;
}) => {
    const notify = useNotify();
    const record = useRecordContext<Sale>();
    const { identity, refetch } = useGetIdentity();
    const { isDirty } = useFormState();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [isLogoDialogOpen, setIsLogoDialogOpen] = useState(false);

    // Add state to track the logo
    const [companyLogo, setCompanyLogo] = useState(record?.company_logo);

    const { mutate: mutateSale } = useMutation({
        mutationKey: ['signup'],
        mutationFn: async (data: Partial<Sale>) => {
            if (!record) {
                throw new Error('Record not found');
            }
            return dataProvider.salesUpdate(record.id, data);
        },
        onSuccess: () => {
            refetch();
            notify('Your profile has been updated');
        },
        onError: () => {
            notify('An error occurred. Please try again.');
        },
    });
    if (!identity) return null;

    const _handleAvatarUpdate = async (values: any) => {
        mutateSale(values);
    };

    const handleCompanyLogoUpdate = async (values: any) => {
        setCompanyLogo(values.company_logo);
        // Immediately save the changes without requiring form submission
        await mutateSale({
            company_logo: values.company_logo,
        });
        setIsLogoDialogOpen(false); // Close the dialog after successful save
    };

    return (
        <Stack gap={4}>
            <Box sx={{ p: 3 }}>
                <Typography
                    sx={{
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        color: '#0f172a',
                        mb: 3,
                    }}
                >
                    Account Settings
                </Typography>

                {/* Company Logo Section */}
                <Box sx={{ mb: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        {/* Hidden ImageEditorField that controls the dialog */}
                        <Box sx={{ display: 'none' }}>
                            <ImageEditorField
                                source="company_logo"
                                type="image"
                                width={100}
                                height={100}
                                aspectRatio={NaN}
                                cropBoxResizable
                                onSave={handleCompanyLogoUpdate}
                                open={isLogoDialogOpen}
                                onClose={() => setIsLogoDialogOpen(false)}
                            />
                        </Box>

                        {/* Visible Image Preview */}
                        <Box
                            sx={{
                                maxWidth: 240,
                                minWidth: 100,
                                minHeight: 60,
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                backgroundColor: '#f8fafc',
                                p: 1,
                            }}
                        >
                            {companyLogo ? (
                                <img
                                    src={
                                        typeof companyLogo === 'string'
                                            ? companyLogo
                                            : (companyLogo as { src: string })
                                                  .src
                                    }
                                    alt="Company Logo"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: 80,
                                        objectFit: 'contain',
                                    }}
                                />
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No logo
                                </Typography>
                            )}
                        </Box>

                        {/* Upload Button */}
                        <Button
                            variant="outlined"
                            startIcon={<Upload size={16} />}
                            onClick={() => setIsLogoDialogOpen(true)}
                            sx={{
                                color: '#10b981',
                                borderColor: '#d1fae5',
                                borderRadius: '8px',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: '#10b981',
                                    backgroundColor: '#ecfdf5',
                                },
                            }}
                        >
                            Upload Agency Logo
                        </Button>
                    </Box>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                    >
                        Image will be used on Hail Pricing Tool Proposal - users
                        typically use Agency Logo
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Image size Limit should be 125kb Max
                    </Typography>
                </Box>

                {/* Profile Section */}
                <Box sx={{ mb: 4 }}>
                    {/* Agent Details */}
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextRender
                                source="first_name"
                                label="First Name"
                                isEditMode={isEditMode}
                                setEditMode={setEditMode}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextRender
                                source="last_name"
                                label="Last Name"
                                isEditMode={isEditMode}
                                setEditMode={setEditMode}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextRender
                                source="email"
                                label="Email Address"
                                isEditMode={isEditMode}
                                setEditMode={setEditMode}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextRender
                                source="agent_number"
                                label="Phone Number"
                                isEditMode={isEditMode}
                                setEditMode={setEditMode}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* Company Info */}
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Agency Information
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextRender
                                source="company_name"
                                label="Agency Name"
                                isEditMode={isEditMode}
                                setEditMode={setEditMode}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextRender
                                source="street"
                                label="Street Address"
                                isEditMode={isEditMode}
                                setEditMode={setEditMode}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextRender
                                source="city"
                                label="City"
                                isEditMode={isEditMode}
                                setEditMode={setEditMode}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextRender
                                source="state"
                                label="State"
                                isEditMode={isEditMode}
                                setEditMode={setEditMode}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextRender
                                source="zip"
                                label="ZIP Code"
                                isEditMode={isEditMode}
                                setEditMode={setEditMode}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* Actions */}
                <Box
                    sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}
                >
                    {isEditMode ? (
                        <>
                            <Button
                                variant="contained"
                                onClick={() => setEditMode(false)}
                                sx={{
                                    mr: 2,
                                    bgcolor: '#e0e0e0', // Light gray background
                                    color: 'black', // Black text
                                    '&:hover': {
                                        bgcolor: '#d0d0d0', // Slightly darker on hover
                                    },
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                type="submit"
                                disabled={!isDirty}
                                sx={{
                                    bgcolor: '#10b981',
                                    color: 'white',
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    '&:hover': {
                                        bgcolor: '#059669',
                                    },
                                    '&.Mui-disabled': {
                                        bgcolor: '#e0e0e0',
                                        color: 'rgba(0, 0, 0, 0.26)',
                                    },
                                    '&:not(.Mui-disabled)': {
                                        color: 'white', // Ensure white text when enabled
                                    },
                                }}
                            >
                                Save Changes
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={() => setEditMode(true)}
                            sx={{
                                bgcolor: '#10b981',
                                color: 'white',
                                borderRadius: '8px',
                                textTransform: 'none',
                                '&:hover': {
                                    bgcolor: '#059669',
                                    color: 'white',
                                },
                            }}
                        >
                            Edit Profile
                        </Button>
                    )}
                </Box>
            </Box>
        </Stack>
    );
};

const TextRender = ({
    source,
    label,
    isEditMode,
    setEditMode,
}: {
    source: string;
    label?: string;
    isEditMode: boolean;
    setEditMode: (value: boolean) => void;
}) => {
    const record = useRecordContext();

    // Add a guard clause to handle undefined record
    if (!record) return null;

    const value = record[source];

    // Phone number formatter
    const formatPhone = (phone: string) => {
        if (!phone) return '';
        const cleaned = phone.replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `(${match[1]}) ${match[2]}-${match[3]}`;
        }
        return phone;
    };

    // Get the display value
    const displayValue = source === 'agent_number' ? formatPhone(value) : value;

    return isEditMode ? (
        <TextInput
            source={source}
            label={label}
            fullWidth
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '& fieldset': {
                        borderColor: '#e2e8f0',
                        borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                        borderColor: '#94a3b8',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: '#10b981',
                        borderWidth: '2px',
                    },
                },
                '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': {
                        color: '#10b981',
                    },
                },
            }}
        />
    ) : (
        <Box
            sx={{
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                p: 2,
                bgcolor: 'white',
                position: 'relative',
                '&:hover .edit-icon': {
                    opacity: 1,
                },
                '&:hover': {
                    borderColor: '#94a3b8',
                },
            }}
        >
            <Labeled label={label}>
                <Typography variant="body1">{displayValue}</Typography>
            </Labeled>
            <IconButton
                className="edit-icon"
                onClick={() => setEditMode(true)}
                sx={{
                    position: 'absolute',
                    top: '50%',
                    right: 8,
                    transform: 'translateY(-50%)',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                }}
            >
                <Pencil size={16} color="#64748b" />
            </IconButton>
        </Box>
    );
};

const _CopyPaste = () => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        setCopied(true);
        navigator.clipboard.writeText(import.meta.env.VITE_INBOUND_EMAIL);
        setTimeout(() => {
            setCopied(false);
        }, 1500);
    };
    return (
        <Tooltip placement="top" title={copied ? 'Copied!' : 'Copy'}>
            <Button
                onClick={handleCopy}
                sx={{
                    textTransform: 'none',
                    justifyContent: 'space-between',
                }}
                endIcon={<Copy size={16} />}
            >
                {import.meta.env.VITE_INBOUND_EMAIL}
            </Button>
        </Tooltip>
    );
};

const PasswordChangeForm = () => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const { identity } = useGetIdentity();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        // Basic validation
        if (newPassword !== confirmPassword) {
            setError('New passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        try {
            if (!identity?.id) {
                throw new Error('User not found');
            }
            await dataProvider.updatePassword(identity.id);
            setSuccess(true);
            notify('Password successfully updated');
            // Clear form
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setError(
                'Failed to update password. Please check your current password and try again.'
            );
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 400 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    Password successfully updated
                </Alert>
            )}

            <TextField
                label="Current Password"
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                fullWidth
                required
                margin="normal"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '& fieldset': {
                            borderColor: '#e2e8f0',
                            borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                            borderColor: '#94a3b8',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#10b981',
                            borderWidth: '2px',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: '#64748b',
                        '&.Mui-focused': {
                            color: '#10b981',
                        },
                    },
                }}
            />

            <TextField
                label="New Password"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                fullWidth
                required
                margin="normal"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '& fieldset': {
                            borderColor: '#e2e8f0',
                            borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                            borderColor: '#94a3b8',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#10b981',
                            borderWidth: '2px',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: '#64748b',
                        '&.Mui-focused': {
                            color: '#10b981',
                        },
                    },
                }}
            />

            <TextField
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                fullWidth
                required
                margin="normal"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '& fieldset': {
                            borderColor: '#e2e8f0',
                            borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                            borderColor: '#94a3b8',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#10b981',
                            borderWidth: '2px',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: '#64748b',
                        '&.Mui-focused': {
                            color: '#10b981',
                        },
                    },
                }}
            />

            <Box sx={{ mt: 3 }}>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                >
                    Password must:
                </Typography>
                <ul style={{ color: 'text.secondary', marginLeft: '20px' }}>
                    <li>Be at least 8 characters long</li>
                    <li>Include at least one uppercase letter</li>
                    <li>Include at least one number</li>
                    <li>Include at least one special character</li>
                </ul>
            </Box>

            <Button
                type="submit"
                variant="contained"
                sx={{
                    mt: 3,
                    bgcolor: '#10b981',
                    color: 'white',
                    borderRadius: '8px',
                    textTransform: 'none',
                    '&:hover': {
                        bgcolor: '#059669',
                    },
                }}
            >
                Update Password
            </Button>
        </Box>
    );
};

const AgentDetailsTab = () => {
    const [selectedStates, setSelectedStates] = useState<string[]>([]);
    const [selectedCarriers, setSelectedCarriers] = useState<string[]>([]);
    const dataProvider = useDataProvider();
    const { identity } = useGetIdentity();
    const notify = useNotify();

    console.log('Current identity:', identity); // Debug log

    // Load existing preferences
    const { data: salesRecord, isLoading } = useGetOne(
        'sales',
        { id: identity?.id ?? '' },
        { enabled: !!identity?.id }
    );

    console.log('Loaded sales record:', salesRecord); // Debug log

    // Initialize state with existing preferences when data loads
    useEffect(() => {
        console.log('Effect triggered with salesRecord:', salesRecord); // Debug log
        if (salesRecord) {
            setSelectedStates(salesRecord.licensed_states || []);
            setSelectedCarriers(salesRecord.appointed_carriers || []);
        }
    }, [salesRecord]);

    const handleSavePreferences = async () => {
        if (!identity?.id) {
            notify('User ID not found', { type: 'error' });
            return;
        }

        try {
            // Get the current record first
            const currentRecord = await dataProvider.getOne('sales', {
                id: identity.id,
            });
            console.log('Current record before update:', currentRecord); // Debug log

            const updateData = {
                ...currentRecord.data,
                licensed_states: selectedStates,
                appointed_carriers: selectedCarriers,
            };

            console.log('Sending update with data:', updateData); // Debug log

            const response = await dataProvider.update('sales', {
                id: identity.id,
                data: updateData,
                previousData: currentRecord.data,
            });

            console.log('Update response:', response); // Debug log

            if (response.data) {
                notify('Preferences saved successfully', { type: 'success' });
            } else {
                throw new Error('No data returned from update');
            }
        } catch (error: unknown) {
            console.error('Save error:', error);
            const errorMessage =
                error instanceof Error ? error.message : 'Unknown error';
            notify(`Error saving preferences: ${errorMessage}`, {
                type: 'error',
            });
        }
    };

    const ChipWithCheckmark = ({
        label,
        selected,
        onClick,
    }: {
        label: string;
        selected: boolean;
        onClick: () => void;
    }) => (
        <Chip
            key={label}
            label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {selected && <Check size={14} />}
                    {label}
                </Box>
            }
            onClick={onClick}
            sx={{
                bgcolor: selected ? '#10b981' : '#f1f5f9',
                color: selected ? 'white' : '#475569',
                '&:hover': {
                    bgcolor: selected ? '#059669' : '#e2e8f0',
                },
                height: 32,
                '& .MuiChip-label': {
                    px: 1,
                },
            }}
        />
    );

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
                States Licensed In
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {ALL_STATES.map(state => (
                    <ChipWithCheckmark
                        key={state}
                        label={state}
                        selected={selectedStates.includes(state)}
                        onClick={() => {
                            const newSelection = selectedStates.includes(state)
                                ? selectedStates.filter(s => s !== state)
                                : [...selectedStates, state];
                            console.log('New states selection:', newSelection); // Debug log
                            setSelectedStates(newSelection);
                        }}
                    />
                ))}
            </Box>

            <Typography variant="h6" gutterBottom>
                Carriers Appointed With
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {ALL_CARRIERS.map(carrier => (
                    <ChipWithCheckmark
                        key={carrier}
                        label={carrier}
                        selected={selectedCarriers.includes(carrier)}
                        onClick={() => {
                            const newSelection = selectedCarriers.includes(
                                carrier
                            )
                                ? selectedCarriers.filter(c => c !== carrier)
                                : [...selectedCarriers, carrier];
                            console.log(
                                'New carriers selection:',
                                newSelection
                            ); // Debug log
                            setSelectedCarriers(newSelection);
                        }}
                    />
                ))}
            </Box>

            <Button
                variant="contained"
                onClick={handleSavePreferences}
                sx={{
                    bgcolor: '#10b981',
                    color: 'white',
                    borderRadius: '8px',
                    textTransform: 'none',
                    '&:hover': {
                        bgcolor: '#059669',
                    },
                }}
            >
                Save Preferences
            </Button>

            <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                    Current States: {selectedStates.join(', ')}
                </Typography>
                <Typography variant="body2">
                    Current Carriers: {selectedCarriers.join(', ')}
                </Typography>
            </Box>
        </Box>
    );
};

// --- Security Tab (MFA Status) ---
const SecurityTab = () => {
    const [factors, setFactors] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const notify = useNotify();

    useEffect(() => {
        const loadFactors = async () => {
            try {
                const verifiedFactors = await getVerifiedFactors();
                setFactors(verifiedFactors);
            } catch (err) {
                console.error('Failed to load MFA factors:', err);
                notify('Failed to load MFA status', {
                    type: 'error',
                });
            } finally {
                setLoading(false);
            }
        };
        loadFactors();
    }, [notify]);

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    p: 4,
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography
                sx={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#0f172a',
                    mb: 1,
                }}
            >
                Two-Factor Authentication
            </Typography>
            <Typography
                sx={{
                    color: '#64748b',
                    mb: 3,
                    fontSize: '0.875rem',
                }}
            >
                Two-factor authentication adds an extra layer of security to
                your account. A verification code from your authenticator app is
                required each time you sign in.
            </Typography>

            {factors.length > 0 ? (
                <Box>
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Two-factor authentication is active.
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                        Authenticator app enrolled on:{' '}
                        {new Date(factors[0].created_at).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Factor name:{' '}
                        {factors[0].friendly_name || 'Authenticator App'}
                    </Typography>
                </Box>
            ) : (
                <Alert severity="warning">
                    No authenticator enrolled. You will be required to set one
                    up on your next sign-in.
                </Alert>
            )}
        </Box>
    );
};

SettingsPage.path = '/settings';
